import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PencilIcon, PlusIcon, XIcon } from 'lucide-react';
import { Button } from '@noor/ui';
import { confirmDialog, modalStore } from '@noor/lib';
import { Can } from '@/entities/access';
import { useChatMacros, type ChatMacro, type MacroLang } from '@/entities/conversation';
import { useDeleteChatMacro } from '../model/use-chat-macros';
import { MacroLangToggle } from './macro-lang-toggle';

interface MacrosPanelProps {
  /** Fired with the macro's text when its chip is clicked. */
  onSelect: (message: string) => void;
}

/**
 * The macros drawer above the composer (cpanel `ChatMacros`): RU/UZ switch,
 * one chip per template (click = use, pencil = edit, × = delete) and an "add"
 * button. Create / edit run in the `chat-macro` modal; delete is confirmed and
 * optimistic.
 */
export function MacrosPanel({ onSelect }: MacrosPanelProps) {
  const { t } = useTranslation('inbox');
  const [lang, setLang] = useState<MacroLang>('ru');
  const { data: macros = [] } = useChatMacros(lang);
  const deleteMacro = useDeleteChatMacro(lang);

  const openEditor = (macro?: ChatMacro) =>
    modalStore.open('chat-macro', { lang, ...(macro ? { macro } : {}) });

  const confirmDelete = async (macro: ChatMacro) => {
    const confirmed = await confirmDialog({
      title: t('macros.deleteTitle'),
      description: t('macros.deleteDescription', { title: macro.title ?? '' }),
      confirmLabel: t('macros.delete'),
      cancelLabel: t('macros.cancel'),
      destructive: true,
    });
    if (confirmed) deleteMacro.mutate(macro.id);
  };

  return (
    <div className="flex max-h-[min(224px,34vh)] flex-col gap-2 border-y border-border bg-muted/60 px-4 pt-2 pb-3">
      <div className="flex shrink-0 items-center justify-between gap-2">
        <div className="flex min-w-0 items-baseline gap-2">
          <span className="text-xs font-extrabold tracking-wider text-foreground uppercase">
            {t('macros.title')}
          </span>
          <span className="truncate text-xs text-muted-foreground">
            {t('macros.hint')}
          </span>
        </div>
        <MacroLangToggle value={lang} onChange={setLang} />
      </div>

      <div className="flex min-h-8 flex-1 flex-wrap content-start gap-1.5 overflow-y-auto">
        {macros.length === 0 ? (
          <span className="text-xs text-muted-foreground">{t('macros.empty')}</span>
        ) : null}
        {macros.map((macro) => (
          <span
            key={String(macro.id)}
            className="inline-flex h-8 items-center gap-0.5 rounded-lg border border-border bg-card pr-1 pl-1 text-sm font-semibold text-foreground shadow-xs transition-colors hover:border-ring/40 hover:bg-muted"
          >
            <Can permission="inbox.macros.manage">
              <button
                type="button"
                aria-label={t('macros.editTitle')}
                className="flex size-6 items-center justify-center rounded text-muted-foreground hover:text-foreground"
                onClick={() => openEditor(macro)}
              >
                <PencilIcon className="size-3.5" />
              </button>
            </Can>
            <button
              type="button"
              className="max-w-48 truncate px-1 focus-visible:outline-none"
              onClick={() => onSelect(macro.message)}
            >
              {macro.title ?? `#${String(macro.id)}`}
            </button>
            <Can permission="inbox.macros.manage">
              <button
                type="button"
                aria-label={t('macros.delete')}
                className="flex size-6 items-center justify-center rounded text-muted-foreground hover:text-destructive"
                onClick={() => void confirmDelete(macro)}
              >
                <XIcon className="size-3.5" />
              </button>
            </Can>
          </span>
        ))}
      </div>

      <Can permission="inbox.macros.manage">
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="w-full shrink-0"
          onClick={() => openEditor()}
        >
          <PlusIcon />
          {t('macros.add')}
        </Button>
      </Can>
    </div>
  );
}
